import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import {
  useLazyGetSpaceAdminQuery,
  useLazyGetSpaceHierarchyAdminQuery,
  useLazyGetSpaceHierarchyQuery,
  useLazyGetSpaceQuery,
} from "../../../redux/api/space/spaceAPI";
import { selectBillingSpaceFilter } from "../../../redux/features/billing-space-filter/billing-space-filter-slice";
import { selectFilter } from "../../../redux/features/filter/filter-slice";
import { selectProfile } from "../../../redux/features/user/user-slice";
import { UserRole } from "../../oversight-core/enums/user-roles";
import { ISpaceView } from "../../oversight-core/interfaces/entities/space";
import { ISpaceShallowHierarchyView } from "../../oversight-core/interfaces/space-shallow-hierarchy-view";
import AppDropDown from "../../oversight-core/ui-elements/app-dropdown/app-drop-down";
import MaterialIcon from "../../oversight-core/ui-elements/material-icon/material-icon";
import FilterSearch from "../filter-search/filter-search";
import styles from "./space-path-view-breadcrumb.module.scss";

interface IProps {
  selectedSpace: { clusterId: string; spaceId: string };
  sendSelectedSpace: (
    space: ISpaceView,
    breadcrumb?: ISpaceShallowHierarchyView[]
  ) => void;
}

const SpacePathViewBreadcrumb = (props: IProps) => {
  const { selectedSpace, sendSelectedSpace } = props;

  const billingSpaceFilter = useSelector(selectBillingSpaceFilter);
  const filtersStore = useSelector(selectFilter);
  const profile = useSelector(selectProfile);
  const [breadCrumbPieces, setBreadCrumbPieces] = useState<
    ISpaceShallowHierarchyView[]
  >([]);

  const [triggerGetSpace, { data: spaceData }] = useLazyGetSpaceQuery();
  const [triggerGetSpaceAdmin, { data: spaceAdminData }] =
    useLazyGetSpaceAdminQuery();
  const [triggerGetHierarchy] = useLazyGetSpaceHierarchyQuery();
  const [triggerGetHierarchyAdmin] = useLazyGetSpaceHierarchyAdminQuery();

  useEffect(() => {
    if (selectedSpace.clusterId && selectedSpace.spaceId) {
      (profile?.role === UserRole.ROLE_USER
        ? triggerGetHierarchy
        : triggerGetHierarchyAdmin)({
        clusterId: selectedSpace.clusterId,
        spaceId: selectedSpace.spaceId,
      })
        .unwrap()
        .then((response) => {
          setBreadCrumbPieces(response.ancestorSpaces);
          (profile?.role === UserRole.ROLE_USER
            ? triggerGetSpace
            : triggerGetSpaceAdmin)({
            clusterId: selectedSpace.clusterId,
            spaceId: selectedSpace.spaceId,
          });
        })
        .catch(() => {
          setBreadCrumbPieces([]);
        });
    }
  }, [
    selectedSpace.clusterId,
    selectedSpace.spaceId,
    billingSpaceFilter.isSpaceClusterListChanged,
    filtersStore.isSpaceListChanged,
    filtersStore.isClear,
    profile?.role,
  ]);

  const getSpace = (id: string) => {
    (profile?.role === UserRole.ROLE_USER
      ? triggerGetSpace
      : triggerGetSpaceAdmin)({
      clusterId: selectedSpace.clusterId,
      spaceId: id,
    })
      .unwrap()
      .then((res) => {
        (profile?.role === UserRole.ROLE_USER
          ? triggerGetHierarchy
          : triggerGetHierarchyAdmin)({
          clusterId: selectedSpace.clusterId,
          spaceId: id,
        })
          .unwrap()
          .then((response) => {
            sendSelectedSpace(
              {
                ...res.subSpaceCluster.rootSpace,
                clusterId: selectedSpace.clusterId,
              },
              response.ancestorSpaces
            );
          });
      });
  };

  return (
    <div className={`${styles.spacePathViewBreadcrumbContainer} p-2`}>
      <Row className="align-items-center justify-content-between">
        <Col className="col-auto">
          <Row className="align-items-center">
            <Col className="col-auto pe-0">
              <MaterialIcon
                icon="apartment"
                color="var(--color-calendar-icon)"
              />
            </Col>
            <Col
              className={`${styles.greaterThan} col-auto ps-2 pe-0`}
            >{`>`}</Col>
            {breadCrumbPieces.map((breadCrumbPiece, index) => {
              return (
                <Col key={breadCrumbPiece.id} className="col-auto pe-2">
                  <Row className="align-items-center">
                    <Col
                      className={`${styles.breadCrumbPath} ${
                        selectedSpace.spaceId === breadCrumbPiece.id &&
                        `${styles.selectedBreadCrumbPath} py-1 px-3 mx-3`
                      } col-auto cursor-pointer`}
                      onClick={() => {
                        getSpace(breadCrumbPiece.id);
                      }}
                    >
                      {`${breadCrumbPiece.name}`}
                    </Col>
                    <Col className={`${styles.greaterThan} col-auto px-0`}>
                      {index === breadCrumbPieces.length - 1 ? "" : ">"}
                    </Col>
                  </Row>
                </Col>
              );
            })}
            {!spaceData ||
            spaceData.subSpaceCluster.rootSpace.childSpaces.length === 0 ? (
              <></>
            ) : (
              <Col className="ps-0">
                <AppDropDown
                  items={spaceData.subSpaceCluster.rootSpace.childSpaces.map(
                    (cs) => {
                      return {
                        text: cs.name,
                        onClick: () => {
                          sendSelectedSpace({
                            ...cs,
                            clusterId: selectedSpace.clusterId,
                          });
                        },
                      };
                    }
                  )}
                  icon="more_horiz"
                />
              </Col>
            )}
            {!spaceAdminData ||
            spaceAdminData.subSpaceCluster.rootSpace.childSpaces.length ===
              0 ? (
              <></>
            ) : (
              <Col className="ps-0">
                <AppDropDown
                  items={spaceAdminData.subSpaceCluster.rootSpace.childSpaces.map(
                    (cs) => {
                      return {
                        text: cs.name,
                        onClick: () => {
                          sendSelectedSpace({
                            ...cs,
                            clusterId: selectedSpace.clusterId,
                          });
                        },
                      };
                    }
                  )}
                  icon="more_horiz"
                />
              </Col>
            )}
          </Row>
        </Col>
        <Col className="col-auto">
          <FilterSearch
            selectedSpace={selectedSpace}
            sendSelectedSpace={sendSelectedSpace}
          />
        </Col>
      </Row>
    </div>
  );
};

export default SpacePathViewBreadcrumb;
